import React from 'react';
import { Link } from 'react-router-dom';
import Icon from '../../components/AppIcon';

import LoginForm from './components/LoginForm';
import SecurityBadges from './components/SecurityBadges';
import LoginBenefits from './components/LoginBenefits';
import TrustIndicators from './components/TrustIndicators';
import SupportSection from './components/SupportSection';

const InstituteLogin = () => {
  return (
    <div className="min-h-screen bg-background">
      <header className="sticky top-0 z-50 bg-card border-b border-border shadow-sm">
        <div className="max-w-[1440px] mx-auto px-4 md:px-6 lg:px-8 h-16 md:h-20 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3 transition-smooth hover-lift">
            <div className="w-10 h-10 md:w-12 md:h-12 bg-primary/10 rounded-lg flex items-center justify-center">
              <Icon name="GraduationCap" size={24} color="var(--color-primary)" />
            </div>
            <span className="text-xl md:text-2xl font-heading font-semibold text-foreground">
              Studyexpo
            </span>
          </Link>

          <nav className="flex items-center gap-2 md:gap-4">
            <Link
              to="/student-registration"
              className="text-sm md:text-base text-muted-foreground hover:text-foreground transition-smooth px-3 py-2"
            >
              Student Portal
            </Link>
            <Link
              to="/admin-dashboard"
              className="text-sm md:text-base text-muted-foreground hover:text-foreground transition-smooth px-3 py-2"
            >
              Admin
            </Link>
          </nav>
        </div>
      </header>
      <main className="max-w-[1440px] mx-auto px-4 md:px-6 lg:px-8 py-8 md:py-12 lg:py-16">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 items-start">
          <div className="order-2 lg:order-1 space-y-8">
            <div>
              <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-primary/10 rounded-full mb-4 md:mb-6">
                <Icon name="LogIn" size={16} color="var(--color-primary)" />
                <span className="text-xs md:text-sm font-medium text-primary">
                  User Portal
                </span>
              </div>
              
              <h1 className="text-3xl md:text-4xl lg:text-5xl font-heading font-bold text-foreground mb-4">
                Welcome Back to Studyexpo
              </h1>
              
              <p className="text-base md:text-lg text-muted-foreground mb-6 md:mb-8">
                Sign in to access your dashboard and connect with educational opportunities.
              </p>

              <TrustIndicators />
            </div>

            <div className="hidden lg:block">
              <LoginBenefits />
            </div>

            <div className="hidden lg:block">
              <SupportSection />
            </div>
          </div>

          <div className="order-1 lg:order-2">
            <div className="bg-card rounded-xl md:rounded-2xl shadow-lg border border-border p-6 md:p-8 lg:p-10">
              <div className="mb-6 md:mb-8">
                <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground mb-2">
                  Sign In
                </h2>
                <p className="text-sm md:text-base text-muted-foreground">
                  Enter your credentials to access your dashboard
                </p>
              </div>

              <LoginForm />

              <div className="mt-8 pt-8 border-t border-border">
                <SecurityBadges />
              </div>
            </div>

            <div className="lg:hidden mt-8 space-y-8">
              <LoginBenefits />
              <SupportSection />
            </div>
          </div>
        </div>

        <div className="mt-12 md:mt-16 lg:mt-20 bg-muted/30 rounded-xl md:rounded-2xl p-6 md:p-8 lg:p-10 border border-border">
          <div className="text-center mb-8">
            <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground mb-3">
              Why Choose Studyexpo?
            </h2>
            <p className="text-sm md:text-base text-muted-foreground max-w-2xl mx-auto">
              Join thousands of users connecting through our innovative virtual education platform
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
            {[
              {
                icon: "Zap",
                title: "Instant Setup",
                description: "Get your virtual booth live in minutes with our intuitive setup wizard"
              },
              {
                icon: "Target",
                title: "Qualified Leads",
                description: "Connect with students genuinely interested in your programs"
              },
              {
                icon: "Clock",
                title: "24/7 Availability",
                description: "Your booth stays open round the clock for global reach"
              },
              {
                icon: "TrendingUp",
                title: "Real-time Analytics",
                description: "Track engagement and optimize your booth performance"
              },
              {
                icon: "Shield",
                title: "Secure Platform",
                description: "Enterprise-grade security with GDPR compliance"
              },
              {
                icon: "Headphones",
                title: "Dedicated Support",
                description: "Expert assistance available whenever you need help"
              }
            ]?.map((feature, index) => (
              <div
                key={index}
                className="flex flex-col items-center text-center p-6 bg-card rounded-lg border border-border hover:border-primary/30 transition-smooth"
              >
                <div className="w-14 h-14 md:w-16 md:h-16 bg-primary/10 rounded-full flex items-center justify-center mb-4">
                  <Icon name={feature?.icon} size={28} color="var(--color-primary)" />
                </div>
                <h3 className="text-base md:text-lg font-semibold text-foreground mb-2">
                  {feature?.title}
                </h3>
                <p className="text-sm text-muted-foreground">
                  {feature?.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </main>
      <footer className="bg-card border-t border-border mt-12 md:mt-16 lg:mt-20">
        <div className="max-w-[1440px] mx-auto px-4 md:px-6 lg:px-8 py-8 md:py-12">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 mb-8">
            <div>
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
                  <Icon name="GraduationCap" size={24} color="var(--color-primary)" />
                </div>
                <span className="text-xl font-heading font-semibold text-foreground">
                  Studyexpo
                </span>
              </div>
              <p className="text-sm text-muted-foreground">
                Connecting students with their dream institutes through innovative virtual fair technology.
              </p>
            </div>

            <div>
              <h3 className="text-sm font-semibold text-foreground mb-4">Quick Links</h3>
              <ul className="space-y-2">
                {['About Us', 'How It Works', 'Pricing', 'FAQ']?.map((link) => (
                  <li key={link}>
                    <a href="#" className="text-sm text-muted-foreground hover:text-foreground transition-smooth">
                      {link}
                    </a>
                  </li>
                ))}
              </ul>
            </div>

            <div>
              <h3 className="text-sm font-semibold text-foreground mb-4">For Institutes</h3>
              <ul className="space-y-2">
                {['Features', 'Success Stories', 'Resources', 'Contact Sales']?.map((link) => (
                  <li key={link}>
                    <a href="#" className="text-sm text-muted-foreground hover:text-foreground transition-smooth">
                      {link}
                    </a>
                  </li>
                ))}
              </ul>
            </div>

            <div>
              <h3 className="text-sm font-semibold text-foreground mb-4">Legal</h3>
              <ul className="space-y-2">
                {['Privacy Policy', 'Terms of Service', 'Cookie Policy', 'GDPR Compliance']?.map((link) => (
                  <li key={link}>
                    <a href="#" className="text-sm text-muted-foreground hover:text-foreground transition-smooth">
                      {link}
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          <div className="pt-8 border-t border-border flex flex-col md:flex-row items-center justify-between gap-4">
            <p className="text-sm text-muted-foreground text-center md:text-left">
              &copy; {new Date()?.getFullYear()} Studyexpo. All rights reserved.
            </p>
            <div className="flex items-center gap-4">
              {['Facebook', 'Twitter', 'Linkedin', 'Instagram']?.map((social) => (
                <a
                  key={social}
                  href="#"
                  className="w-9 h-9 bg-muted rounded-lg flex items-center justify-center hover:bg-primary/10 transition-smooth"
                  aria-label={social}
                >
                  <Icon name={social} size={18} color="var(--color-muted-foreground)" />
                </a>
              ))}
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default InstituteLogin;